/**
 * StructuredDataDrawer — right-side, non-modal Sheet showing the REAL composed
 * JSON-LD graph that would ship at publish, with per-type ship status, per-field
 * source provenance, and the shadow postgen_jsonld enrichment blob in a
 * clearly-fenced "not shipped" block.
 *
 * T4 redesign (Carl's ruling: locked semantics, NO chips) — the previous
 * "Schema types" chip section is gone. Per-type value rows for ALL 5 schema
 * types (Article, BreadcrumbList, HowTo, FAQPage, Product) are folded into the
 * composed-graph section. HowTo selection is implicit (authored content
 * present or user forced). FAQPage is read-only ("N questions detected") from
 * the preview jsonld. Product is a placeholder.
 *
 * Padlocks are banned here: one icon may never carry both "editability"
 * and "shipping behavior". Shipping behavior is signified by quiet muted
 * text tags in the row headers (ShipsTag): Article "ships at publish",
 * BreadcrumbList "automatic · always ships", FAQPage "automatic — from your
 * FAQ blocks". Editability stays signified by the visible controls
 * themselves (textarea, suggest buttons).
 *
 * The backend schema-preview endpoint is the SINGLE SOURCE OF TRUTH for schema
 * gating (willShip / reason / field sources / selection_active). The frontend
 * never re-derives these — it only renders from the endpoint payload.
 *
 * Fail-soft: if the endpoint errors, the drawer still opens with an honest
 * "couldn't load" state. Non-modal: the editor surface stays interactive
 * (no overlay, no close-on-outside-click) so the operator can compare the
 * graph against the article body.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  Braces,
  ChevronDown,
  Copy,
  ClipboardCheck,
  Check,
  Sparkles,
  AlertTriangle,
  Info,
  Loader2,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useSchemaPreview } from '@/hooks/useBlogCreationAPI';
import type {
  SchemaPreviewResponse,
  SchemaFieldSource,
} from '@/services/blogCreationService';
import type { SchemaFocusType } from './cards/StructuredDataCard';
import { TYPE_LABELS } from './schemaTypeLabels';
import { AiSuggestButton } from './cards/AiSuggestButton';
import { MetadataFieldChip } from './cards/MetadataFieldChip';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** HowTo object shape — authored, ships when its flag is on. */
export interface HowToObject {
  name: string;
  steps: { name: string; text: string }[];
}

/**
 * A single BreadcrumbList particle (position is 1-based: Home, Blog, <post>).
 * `url` is optional — the backend persists name+position only (publish
 * always re-derives the breadcrumb fresh at compose time; this drawer's
 * trail display is names-only, so a persisted url has no consumer here).
 */
export interface BreadcrumbParticle {
  name: string;
  url?: string | null;
  position: number;
}

/** A single FAQ question/answer pair, body-derived. */
export interface FaqPair {
  question: string;
  answer: string;
}

/** The FAQ packet authored by the postgen_schema run — derived, not user-written. */
export interface AuthoredFaq {
  has_faq: boolean;
  pairs: FaqPair[];
}

/** Authored schema fields written into generation_metadata.postgen_schema.fields. */
export interface AuthoredSchema {
  article_description: string;
  is_howto: boolean;
  howto: HowToObject | null;
  /** Mechanical Home › Blog › <post> trail. Optional/additive — falls back to the live preview graph when absent. */
  breadcrumbs?: BreadcrumbParticle[] | null;
  /** Body-derived FAQ pairs. Optional/additive — falls back to the live preview graph when absent. */
  faq?: AuthoredFaq | null;
}

/** Discriminant for which authored field is being edited/suggested. */
export type AuthoredField = 'article_description' | 'howto';

/** State of the async family-E schema-authoring run, surfaced for "authoring…" UI. */
export type SchemaRunStatus = 'queued' | 'running' | 'applied' | 'failed' | null;

export interface StructuredDataDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentId: string | null;
  /** Shadow enrichment blob (generation_metadata.postgen_jsonld). */
  enrichment: object | null;
  /**
   * Kicks off a metadata run that (re)authors the schema enrichment
   * (postgen_schema: article description + HowTo). Wired to the same async
   * regenerate the metadata control uses — schema is authored when the
   * POSTGEN_SCHEMA_METADATA_ENABLED flag is on for the environment.
   */
  onGenerateSchema?: () => void;
  /** True while a metadata/schema run request is in flight. */
  generating?: boolean;
  /**
   * Authored schema fields (article_description + HowTo) from
   * generation_metadata.postgen_schema.fields. When provided, the drawer
   * renders editable rows for Article.description and the HowTo editor.
   */
  authoredSchema?: AuthoredSchema | null;
  /**
   * Persist an authored field edit. Caller MUST debounce (e.g. 800ms) and
   * write into generation_metadata.postgen_schema.fields with an edited
   * stamp so the family-E re-run cannot clobber it.
   */
  onEditField?: (field: AuthoredField, value: string | HowToObject) => void;
  /**
   * Force a HowTo on a non-procedural article. Returns the HowTo OBJECT for
   * PREVIEW. Accept persists via onEditField('howto', obj); Reject discards.
   * (Kept — AiSuggestButton handles string fields only; HowTo needs its own
   * PREVIEW→ACCEPT flow.)
   */
  onForceHowTo?: () => Promise<HowToObject | null>;
  /**
   * Status of the async family-E schema run. While 'queued' or 'running',
   * the per-type rows show an "authoring…" state and disable edits; the
   * parent refetches and feeds fresh authoredSchema when the run flips
   * to 'applied'.
   */
  schemaRunStatus?: SchemaRunStatus;
  /**
   * Tenant identifier — required to wire AiSuggestButton for the
   * Article.description row. When absent (the current mount does not pass
   * it yet), the AI-suggest affordance is omitted; the description is still
   * editable as a plain textarea. Optional so the existing mount invocation
   * (Session C) keeps type-checking without changes.
   */
  tenantId?: string;
  /**
   * Scoped open: the schema type a rail-card row tap asked to focus. With a
   * focus, exactly that type's panel is expanded (strong "you are here"
   * treatment); without one (the "Edit details" button), all five panels
   * are collapsed to value-bearing heading rows. The accordion reset is
   * keyed on the open/focusType transition — never persisted. The mount
   * clears this when the drawer closes.
   */
  focusType?: SchemaFocusType | null;
  /**
   * Postel prefill source for an EMPTY article-summary socket (the mount
   * passes metaDescription || excerpt). It is offered ONLY as an explicit
   * "Use meta description" click that routes through onEditField — never a
   * silent write, since edits persist to the DB. No client-side
   * validation/normalization is applied to it (liberal input is the design
   * ruling; schema.org normalization belongs to the publish path, backend).
   */
  fallbackDescription?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'schema-drawer-width';
const MIN_WIDTH = 640;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 680;

const SOURCE_LABELS: Record<SchemaFieldSource, string> = {
  site_fact: 'site fact',
  authored: 'authored',
  enrichment: 'enrichment',
  tracked: 'tracked',
};

/** All 5 schema types rendered as per-type rows, in display order. */
const PER_TYPE_ROWS = ['Article', 'BreadcrumbList', 'HowTo', 'FAQPage', 'Product'] as const;

type PerTypeName = (typeof PER_TYPE_ROWS)[number];

/** Collapse a value to a single-line presence snippet for accordion headings. */
function oneLine(text: string, max = 56): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/**
 * Render a breadcrumb trail with the current page (last segment) bolded by
 * WEIGHT only — never by color, so the muted derived style stays the
 * load-bearing read-only cue.
 */
function trailWithCurrentBold(trail: string): React.ReactNode {
  const parts = trail.split(' › ');
  if (parts.length < 2) return trail;
  return (
    <>
      {parts.slice(0, -1).join(' › ')} ›{' '}
      <span className="font-semibold">{parts[parts.length - 1]}</span>
    </>
  );
}

/**
 * Heading ships-tags: shipping behavior in WORDS, never a padlock. They
 * double as the positive read-only/derived cue on the automatic rows
 * (BreadcrumbList, FAQPage).
 */
const SHIPS_LABELS: Partial<Record<PerTypeName, string>> = {
  Article: 'ships at publish',
  BreadcrumbList: 'automatic · always ships',
  FAQPage: 'automatic — from your FAQ blocks',
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** A small source-tag pill with coral/teal token coloring. */
function SourceTag({ source, ships }: { source: SchemaFieldSource; ships: boolean }) {
  const isTracked = source === 'tracked';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide border',
        isTracked
          ? 'bg-coral-light text-coral-dark border-coral/30'
          : 'bg-teal-light text-teal-dark border-teal/30',
      )}
      title={
        isTracked
          ? 'Tracked shadow field — does NOT ship to WordPress'
          : ships
            ? 'Ships at publish'
            : 'Authored field (ships when its flag is on)'
      }
    >
      {SOURCE_LABELS[source]}
    </span>
  );
}

/** One node in the composed @graph, rendered as a collapsible card. */
function GraphNodeCard({
  node,
  fieldMap,
}: {
  node: Record<string, unknown>;
  fieldMap: Map<string, { source: SchemaFieldSource; ships: boolean }>;
}) {
  const [expanded, setExpanded] = useState(true);
  const type = typeof node['@type'] === 'string' ? (node['@type'] as string) : 'Schema';

  const entries = useMemo(() => {
    return Object.entries(node).filter(([key]) => key !== '@context');
  }, [node]);

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-warm-bg/50 hover:bg-warm-bg border-0 cursor-pointer text-left transition-colors"
      >
        <ChevronDown
          className={cn(
            'size-3.5 text-muted-foreground transition-transform',
            expanded ? 'rotate-0' : '-rotate-90',
          )}
        />
        <span className="text-[12px] font-bold text-foreground">{type}</span>
        <span className="text-[10.5px] text-muted-foreground">
          {entries.length} field{entries.length !== 1 ? 's' : ''}
        </span>
      </button>
      {expanded && (
        <div className="px-3 py-2 space-y-1">
          {entries.map(([key, value]) => {
            const provenance = fieldMap.get(`${type}.${key}`);
            const source = provenance?.source ?? 'site_fact';
            const ships = provenance?.ships ?? true;
            const valueStr = typeof value === 'object'
              ? JSON.stringify(value)
              : String(value);
            return (
              <div key={key} className="flex items-start gap-2 py-0.5">
                <SourceTag source={source} ships={ships} />
                <span className="text-[11px] font-mono font-medium text-foreground shrink-0">
                  {key}:
                </span>
                <span className="text-[11px] font-mono text-muted-foreground break-all">
                  {valueStr}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-type rows — folded into the composed-graph section (T4 redesign).
// Ship semantics via quiet word tags (ShipsTag), never a padlock.
// ---------------------------------------------------------------------------

/**
 * Shipping behavior must be signified by WORDS, never a padlock — one icon
 * may not carry both editability and shipping semantics. A quiet muted text
 * tag in the row header; it doubles as the positive read-only/derived cue
 * on the automatic rows (BreadcrumbList, FAQPage).
 */
function ShipsTag({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center text-[10px] text-muted-foreground"
      data-testid="ships-tag"
      title="Added by this platform on every publish"
    >
      {label}
    </span>
  );
}

/**
 * Reviewer-words row header — the TYPE_LABELS outcome label (bold) with the
 * schema.org type demoted to a small muted secondary tag. Same source of
 * truth as the rail card (`rail/schemaTypeLabels.ts`).
 */
function RowHeaderLabel({ typeName }: { typeName: keyof typeof TYPE_LABELS }) {
  const { label, tag } = TYPE_LABELS[typeName];
  return (
    <span className="inline-flex items-baseline gap-1.5 text-[12px] font-bold text-foreground">
      {label}
      <span className="text-[9.5px] font-normal text-muted-foreground">{tag}</span>
    </span>
  );
}

/**
 * The empty-state signature device: a dashed slot with "No value yet" and the
 * fill action INSIDE it. Accepting a suggestion (or generating) fills this
 * exact slot in place — the empty state is the invitation to act.
 */
function EmptySocket({
  label = 'No value yet',
  children,
  testId,
}: {
  label?: string;
  children?: React.ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border bg-warm-bg/60 px-2.5 py-2"
      data-testid={testId ?? 'empty-socket'}
    >
      <span className="text-[11px] italic text-muted-foreground">{label}</span>
      {children && <div className="flex items-center gap-1.5 shrink-0">{children}</div>}
    </div>
  );
}

/** Read-only deterministic field row, derived from the preview @graph node. */
function ReadOnlyFieldRow({
  label,
  value,
  fieldMap,
  schemaType,
}: {
  label: string;
  value: unknown;
  fieldMap: Map<string, { source: SchemaFieldSource; ships: boolean }>;
  schemaType: string;
}) {
  const provenance = fieldMap.get(`${schemaType}.${label}`);
  const source = provenance?.source ?? 'site_fact';
  const ships = provenance?.ships ?? true;
  const valueStr =
    value == null
      ? ''
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
  return (
    <div className="flex items-start gap-2 py-0.5">
      <SourceTag source={source} ships={ships} />
      <span className="text-[11px] font-mono font-medium text-foreground shrink-0">
        {label}:
      </span>
      <span className="text-[11px] font-mono text-muted-foreground break-all">
        {valueStr || '(empty)'}
      </span>
    </div>
  );
}

const EDIT_INPUT_CLASSES =
  'w-full px-2.5 py-1 text-[12px] border border-border rounded-md bg-card outline-none ' +
  'transition-shadow focus:border-coral focus:ring-2 focus:ring-coral/20 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

const TEXTAREA_CLASSES = `${EDIT_INPUT_CLASSES} resize-y`;

/**
 * Force-HowTo button styling — consolidated locally (AiSuggestButton handles
 * only string fields; HowTo returns an object so it keeps its own PREVIEW
 * flow). The old per-purpose constants (SUGGEST_BTN_CLASSES/ACCEPT/REJECT/
 * PREVIEW_BOX) were removed when article_description moved to AiSuggestButton;
 * these two remain only for the Force-HowTo preview.
 */
/**
 * The HowTo suggest control is a QUIET action (loud coral pills get
 * banner-blindness-filtered here) — inline text action, muted → foreground
 * on hover, sparkles size-3, no pill/border. Same vocabulary as
 * AiSuggestButton's 'quiet' variant.
 */
const FORCE_BTN_CLASSES =
  'inline-flex items-center gap-1 text-[11px] ' +
  'bg-transparent border-0 text-muted-foreground hover:text-foreground ' +
  'cursor-pointer motion-safe:transition-colors ' +
  'focus:outline-none focus-visible:ring-1 focus-visible:ring-teal ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-muted-foreground';

const FORCE_ACCEPT_CLASSES =
  'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-semibold ' +
  'bg-teal-light text-teal-dark border border-teal/30 hover:bg-teal/10 ' +
  'cursor-pointer motion-safe:transition-colors ' +
  'focus:outline-none focus-visible:ring-1 focus-visible:ring-teal';

const FORCE_REJECT_CLASSES =
  'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-semibold ' +
  'bg-card text-muted-foreground border border-border hover:text-foreground ' +
  'cursor-pointer motion-safe:transition-colors ' +
  'focus:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground';

const FORCE_PREVIEW_CLASSES =
  'rounded-md border border-teal/30 bg-teal-light/40 p-2 space-y-1.5';

/**
 * Labeled async skeleton — the wait must be NAMED, never a bare disabled
 * socket. Shown inside the actionable sockets while the family-E authoring
 * run is queued/running.
 */
function AuthoringSkeleton({ label, testId }: { label: string; testId: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-dashed border-border bg-warm-bg/60 px-2.5 py-2"
      data-testid={testId}
    >
      <span
        aria-hidden="true"
        className="h-2.5 w-16 rounded-full bg-muted-foreground/25 motion-safe:animate-pulse"
      />
      <span className="text-[11px] italic text-muted-foreground">{label}</span>
    </div>
  );
}

/** Local UI state for a Force-HowTo preview (current→suggested→Accept/Reject). */
type HowToPreviewState =
  | { state: 'idle'; suggested: null }
  | { state: 'loading'; suggested: null }
  | { state: 'previewing'; suggested: HowToObject };

const HOWTO_PREVIEW_IDLE = { state: 'idle', suggested: null } as const;

/**
 * HowToEditor — authored HowTo (name + step text) editor + Force-HowTo
 * preview→accept flow. Lifted out of the legacy AuthoredSchemaSection when
 * the per-type rows were introduced; the chip-driven HowTo selection is gone
 * (implicit: has authored content / user forced it).
 */
function HowToEditor({
  authoredSchema,
  onEditField,
  onForceHowTo,
  schemaRunStatus,
}: {
  authoredSchema: AuthoredSchema;
  onEditField?: (field: AuthoredField, value: string | HowToObject) => void;
  onForceHowTo?: () => Promise<HowToObject | null>;
  schemaRunStatus?: SchemaRunStatus;
}) {
  const isAuthoring = schemaRunStatus === 'queued' || schemaRunStatus === 'running';

  // ---- HowTo: local mirror + debounced persist ----
  const [howtoValue, setHowtoValue] = useState<HowToObject | null>(authoredSchema.howto);
  const [howtoPropSnapshot, setHowtoPropSnapshot] = useState(authoredSchema.howto);
  if (authoredSchema.howto !== howtoPropSnapshot) {
    setHowtoPropSnapshot(authoredSchema.howto);
    setHowtoValue(authoredSchema.howto);
  }

  const howtoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (howtoTimerRef.current) clearTimeout(howtoTimerRef.current);
    };
  }, []);

  const scheduleHowtoSave = (next: HowToObject) => {
    setHowtoValue(next);
    if (howtoTimerRef.current) clearTimeout(howtoTimerRef.current);
    howtoTimerRef.current = setTimeout(() => {
      onEditField?.('howto', next);
    }, 800);
  };

  // ---- Force HowTo (PREVIEW → ACCEPT) ----
  const [howtoPreview, setHowtoPreview] = useState<HowToPreviewState>(HOWTO_PREVIEW_IDLE);

  const handleForceHowTo = async () => {
    if (!onForceHowTo || howtoPreview.state === 'loading') return;
    setHowtoPreview({ state: 'loading', suggested: null });
    try {
      const obj = await onForceHowTo();
      if (obj && Array.isArray(obj.steps) && obj.steps.length >= 2) {
        setHowtoPreview({ state: 'previewing', suggested: obj });
      } else {
        setHowtoPreview(HOWTO_PREVIEW_IDLE);
      }
    } catch {
      setHowtoPreview(HOWTO_PREVIEW_IDLE);
    }
  };

  // Accept is a HARD INTERLOCK: a suggestion persists ONLY via the
  // explicit Accept path (onEditField) — no auto-apply anywhere. The ships
  // line is persistent; the reject note is transient (~2.5s, aria-live
  // polite, muted).
  const [acceptedShipsName, setAcceptedShipsName] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState(false);
  const rejectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
    };
  }, []);

  const acceptForcedHowTo = () => {
    if (howtoPreview.state !== 'previewing' || howtoPreview.suggested == null) return;
    const obj = howtoPreview.suggested;
    onEditField?.('howto', obj);
    setHowtoValue(obj);
    setHowtoPreview(HOWTO_PREVIEW_IDLE);
    setAcceptedShipsName(obj.name);
  };

  const rejectForcedHowTo = () => {
    setHowtoPreview(HOWTO_PREVIEW_IDLE);
    setRejectNote(true);
    if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
    rejectTimerRef.current = setTimeout(() => setRejectNote(false), 2500);
  };

  // Persistent accept confirmation under the editor.
  const shipsLine = acceptedShipsName && (
    <p data-testid="howto-ships-line" className="mt-1.5 text-[11px] text-teal-dark">
      Now ships as How-to: “{acceptedShipsName}”
    </p>
  );

  // Transient reject feedback.
  const rejectNoteLine = rejectNote && (
    <p
      data-testid="reject-note"
      aria-live="polite"
      className="mt-1 text-[10.5px] text-muted-foreground"
    >
      Nothing changed — not shipping.
    </p>
  );

  if (authoredSchema.is_howto && howtoValue) {
    return (
      <div className="space-y-1.5 pl-4" data-testid="howto-editor">
        {shipsLine}
        {rejectNoteLine}
        <input
          type="text"
          value={howtoValue.name}
          onChange={(e) => scheduleHowtoSave({ ...howtoValue, name: e.target.value })}
          disabled={isAuthoring}
          aria-label="HowTo name"
          className={EDIT_INPUT_CLASSES}
          data-testid="howto-name-input"
        />
        {howtoValue.steps.map((step, idx) => (
          <div key={idx} className="space-y-0.5">
            <div className="text-[10.5px] font-semibold text-muted-foreground">
              {idx + 1}. {step.name}
            </div>
            <input
              type="text"
              value={step.text}
              onChange={(e) => {
                const next: HowToObject = {
                  ...howtoValue,
                  steps: howtoValue.steps.map((s, i) =>
                    i === idx ? { ...s, text: e.target.value } : s,
                  ),
                };
                scheduleHowtoSave(next);
              }}
              disabled={isAuthoring}
              aria-label={`Step ${idx + 1} text`}
              className={EDIT_INPUT_CLASSES}
              data-testid={`howto-step-text-${idx}`}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pl-4" data-testid="howto-forced-section">
      {onForceHowTo && (
        <button
          type="button"
          onClick={handleForceHowTo}
          disabled={isAuthoring || howtoPreview.state === 'loading'}
          title="Suggest HowTo steps for this article"
          className={FORCE_BTN_CLASSES}
          data-testid="force-howto-btn"
        >
          {howtoPreview.state === 'loading' ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Sparkles className="size-3" />
          )}
          {/* Verb-first quiet label; "Force HowTo" was builder jargon. */}
          Suggest steps
        </button>
      )}
      {howtoPreview.state === 'previewing' && howtoPreview.suggested && (
        <div className={`${FORCE_PREVIEW_CLASSES} mt-1.5`} data-testid="howto-preview">
          <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            Suggested HowTo
          </span>
          <p className="text-[11.5px] font-semibold text-foreground leading-snug">
            {howtoPreview.suggested.name}
          </p>
          <ol className="list-decimal pl-4 space-y-0.5 text-[11px] text-muted-foreground">
            {howtoPreview.suggested.steps.map((s, i) => (
              <li key={i}>
                <span className="font-semibold text-foreground">{s.name}</span>
                {s.text ? ` — ${s.text}` : ''}
              </li>
            ))}
          </ol>
          <div className="flex items-center gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={acceptForcedHowTo}
              className={FORCE_ACCEPT_CLASSES}
              data-testid="howto-accept"
            >
              <Check className="size-3" />
              Accept
            </button>
            <button
              type="button"
              onClick={rejectForcedHowTo}
              className={FORCE_REJECT_CLASSES}
              data-testid="howto-reject"
            >
              <X className="size-3" />
              Reject
            </button>
          </div>
        </div>
      )}
      {shipsLine}
      {rejectNoteLine}
    </div>
  );
}

/**
 * Count FAQPage mainEntity entries in the preview @graph. Returns 0 when there
 * is no FAQPage node or mainEntity is missing/empty. Used for the read-only
 * "N questions detected" row (FAQ authoring is deferred to backlog).
 */
function countFaqQuestions(jsonld: Array<Record<string, unknown>> | null): number {
  if (!jsonld) return 0;
  for (const node of jsonld) {
    if (node['@type'] === 'FAQPage') {
      const main = node['mainEntity'];
      if (Array.isArray(main)) return main.length;
      if (main && typeof main === 'object') return 1;
      return 0;
    }
  }
  return 0;
}

/**
 * Find a node by @type in the preview @graph. Returns the first match.
 */
function findNode(
  jsonld: Array<Record<string, unknown>> | null,
  type: string,
): Record<string, unknown> | null {
  if (!jsonld) return null;
  for (const node of jsonld) {
    if (node['@type'] === type) return node;
  }
  return null;
}

/**
 * PerTypeRows — renders all 5 schema types as rows inside the composed-graph
 * section. Each row has a header (type name + status pill) and type-specific
 * content:
 *   - Article: deterministic fields read-only + article_description field row
 *     using AiSuggestButton in preview mode + MetadataFieldChip provenance.
 *     ShipsTag "ships at publish" — always ships (server-enforced in T3),
 *     while the description stays EDITABLE (the ship tag and the edit
 *     controls deliberately coexist; S1 split the signifiers).
 *   - BreadcrumbList: read-only derived trail, no interaction; ShipsTag
 *     "automatic · always ships" is the positive read-only cue.
 *   - HowTo: authored editor (name + step text + Force HowTo) STAYS; the
 *     chip DIES — selection is implicit.
 *   - FAQPage: read-only "N questions detected" + pointer to edit FAQ blocks
 *     in the article body. No suggest.
 *   - Product: placeholder ("needs backend authored slot — backlog"), no suggest.
 */
function PerTypeRows({
  preview,
  fieldMap,
  authoredSchema,
  onEditField,
  onForceHowTo,
  schemaRunStatus,
  contentId,
  tenantId,
  onGenerateSchema,
  generating,
  focusType,
  fallbackDescription,
}: {
  preview: SchemaPreviewResponse | null | undefined;
  fieldMap: Map<string, { source: SchemaFieldSource; ships: boolean }>;
  authoredSchema?: AuthoredSchema | null;
  onEditField?: (field: AuthoredField, value: string | HowToObject) => void;
  onForceHowTo?: () => Promise<HowToObject | null>;
  schemaRunStatus?: SchemaRunStatus;
  contentId: string | null;
  tenantId?: string;
  onGenerateSchema?: () => void;
  generating?: boolean;
  focusType?: SchemaFocusType | null;
  fallbackDescription?: string;
}) {
  const isAuthoring = schemaRunStatus === 'queued' || schemaRunStatus === 'running';
  const articleNode = findNode(preview?.jsonld ?? null, 'Article');

  // FAQ count — prefer the persisted authored packet (rides on ContentDetail,
  // renders without opening/refetching the preview); fall back to the lazy
  // schema-preview count so nothing regresses before the packet is persisted.
  const faqQuestionCount =
    authoredSchema?.faq != null
      ? authoredSchema.faq.pairs.length
      : countFaqQuestions(preview?.jsonld ?? null);

  // Breadcrumb "value" — prefer the persisted authored particles (immediate,
  // no preview fetch needed); fall back to the actual derived trail from the
  // composed graph (Home › Blog › {title}); fall back to a generic trail
  // before either source has loaded.
  const breadcrumbNode = findNode(preview?.jsonld ?? null, 'BreadcrumbList');
  // Reads the schema.org-standard NESTED ListItem shape emitted by
  // SchemaBuilder.build_breadcrumb_schema: itemListElement[].item.name,
  // sorted by position — NOT a flat itemListElement[].name (see the sibling
  // accessor's comment in UniversalEditorRailMount.tsx for why this matters:
  // this preview fallback is the ACTIVE path whenever authoredSchema.breadcrumbs
  // is absent, i.e. all of prod today).
  const breadcrumbTrailFromPreview = (() => {
    const items = breadcrumbNode?.itemListElement;
    if (!Array.isArray(items) || items.length === 0) return null;
    const crumbs = items
      .map((it) => {
        if (!it || typeof it !== 'object') return null;
        const li = it as { position?: unknown; item?: { name?: unknown } };
        const name = li.item && typeof li.item === 'object' ? li.item.name : undefined;
        if (typeof name !== 'string' || name.length === 0) return null;
        const position = typeof li.position === 'number' ? li.position : Number.MAX_SAFE_INTEGER;
        return { name, position };
      })
      .filter((c): c is { name: string; position: number } => c !== null);
    if (crumbs.length === 0) return null;
    crumbs.sort((a, b) => a.position - b.position);
    return crumbs.map((c) => c.name).join(' › ');
  })();
  const breadcrumbTrailFromParticles = (() => {
    const particles = authoredSchema?.breadcrumbs;
    if (!particles || particles.length === 0) return null;
    const names = [...particles]
      .sort((a, b) => a.position - b.position)
      .map((p) => p.name)
      .filter((n) => n.length > 0);
    return names.length > 0 ? names.join(' › ') : null;
  })();
  const breadcrumbTrail = breadcrumbTrailFromParticles ?? breadcrumbTrailFromPreview;

  // ---- article_description: local mirror + debounced persist ----
  // React-recommended "store previous prop" pattern so an external change
  // (auto-fill on run, parent accept, etc) re-syncs the textarea without
  // calling setState inside an effect.
  const initialDesc = authoredSchema?.article_description ?? '';
  const [descValue, setDescValue] = useState(initialDesc);
  const [descPropSnapshot, setDescPropSnapshot] = useState(initialDesc);
  if ((authoredSchema?.article_description ?? '') !== descPropSnapshot) {
    const next = authoredSchema?.article_description ?? '';
    setDescPropSnapshot(next);
    setDescValue(next);
  }

  const descTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Socket → textarea swap: the description renders as an EmptySocket until a
  // value exists (suggest-accept or manual). "write manually" opens the
  // textarea without a value so typing is still possible from the socket.
  const hasDescValue = Boolean(descValue && descValue.trim());
  const [manualDescOpen, setManualDescOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (descTimerRef.current) clearTimeout(descTimerRef.current);
    };
  }, []);

  const handleDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setDescValue(v);
    if (descTimerRef.current) clearTimeout(descTimerRef.current);
    descTimerRef.current = setTimeout(() => {
      onEditField?.('article_description', v);
    }, 800);
  };

  // description provenance for the MetadataFieldChip — categorical, never a
  // score or model name. AI-authored when present (postgen_schema run); falls
  // back to no chip when empty.
  const descChipState =
    descValue && descValue.trim()
      ? { field: 'article_description', source: 'ai' as const, needsReview: false }
      : undefined;

  const canSuggestDesc = Boolean(contentId && tenantId);

  // ---- Scoped open: progressive-disclosure accordion ----
  // Reset is keyed on the focusType transition, NEVER persisted: the
  // drawer's Sheet content unmounts on close, so each open session starts
  // fresh from the current focus; a focus change while open resets the
  // accordion to the new focus. With focus: exactly that panel expanded
  // (strong "you are here" treatment on the row). Without focus (Edit
  // details): all five collapsed to VALUE-BEARING heading rows — a collapsed
  // heading still shows its presence one-liner (never information-free).
  // Headings are chevron toggles; multiple panels may be open after user
  // interaction.
  const [accordion, setAccordion] = useState<{
    focus: SchemaFocusType | null;
    expanded: SchemaFocusType[];
  }>(() => ({ focus: focusType ?? null, expanded: focusType ? [focusType] : [] }));
  if (accordion.focus !== (focusType ?? null)) {
    setAccordion({ focus: focusType ?? null, expanded: focusType ? [focusType] : [] });
  }
  const togglePanel = (t: SchemaFocusType) => {
    setAccordion((prev) => ({
      focus: focusType ?? null,
      expanded: prev.expanded.includes(t)
        ? prev.expanded.filter((x) => x !== t)
        : [...prev.expanded, t],
    }));
  };

  const howToStepCount = authoredSchema?.is_howto
    ? authoredSchema.howto?.steps?.length ?? 0
    : 0;

  /** The presence one-liner every collapsed heading carries (value-bearing). */
  const presenceLines: Record<(typeof PER_TYPE_ROWS)[number], React.ReactNode> = {
    Article: descValue.trim() ? `“${oneLine(descValue)}”` : 'no value yet',
    BreadcrumbList: trailWithCurrentBold(breadcrumbTrail ?? 'Home › Blog › this article'),
    HowTo:
      howToStepCount > 0
        ? `${howToStepCount} step${howToStepCount !== 1 ? 's' : ''}`
        : 'no value yet',
    FAQPage:
      faqQuestionCount >= 2
        ? `${faqQuestionCount} questions detected`
        : faqQuestionCount === 1
          ? // Publish gates FAQPage at ≥2 pairs — a single question is a
            // won't-ship state, never a shipping value.
            '1 question found — needs at least 2 to ship'
          : 'No FAQ blocks in the article',
    Product: 'Not available yet',
  };

  // ---- Panel bodies (same content as the pre-S4 flat rows; the old row
  //      headers move up into the accordion heading) ----

  const articleBody = (
    <div className="space-y-1">
      {/* deterministic fields read-only from preview @graph */}
      {articleNode && (
        <>
          {articleNode.headline != null && (
            <ReadOnlyFieldRow
              label="headline"
              value={articleNode.headline}
              fieldMap={fieldMap}
              schemaType="Article"
            />
          )}
          {/* The url row must stay visible: UUID-slug defects in the live
              graph are exactly what a reviewer catches here. Always the REAL
              preview-endpoint value, never a placeholder. */}
          {articleNode.url != null && (
            <ReadOnlyFieldRow
              label="url"
              value={articleNode.url}
              fieldMap={fieldMap}
              schemaType="Article"
            />
          )}
          {articleNode.datePublished != null && (
            <ReadOnlyFieldRow
              label="datePublished"
              value={articleNode.datePublished}
              fieldMap={fieldMap}
              schemaType="Article"
            />
          )}
          {articleNode.image != null && (
            <ReadOnlyFieldRow
              label="image"
              value={articleNode.image}
              fieldMap={fieldMap}
              schemaType="Article"
            />
          )}
          {articleNode.publisher != null && (
            <ReadOnlyFieldRow
              label="publisher"
              value={articleNode.publisher}
              fieldMap={fieldMap}
              schemaType="Article"
            />
          )}
        </>
      )}

      {/* article_description — the value IS the status. Populated →
          the editable textarea (+ Suggest to replace). Empty → the
          EmptySocket with the Suggest action INSIDE it; accepting
          fills this exact slot in place. */}
      <div className="space-y-1 pt-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label
            htmlFor="article-description-row"
            className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground"
          >
            description
          </label>
          {hasDescValue && (
            <div className="flex items-center gap-1.5">
              <MetadataFieldChip state={descChipState} />
              {canSuggestDesc && (
                <AiSuggestButton
                  field="article_description"
                  onSuggest={(v) => {
                    if (descTimerRef.current) clearTimeout(descTimerRef.current);
                    setDescValue(v);
                    onEditField?.('article_description', v);
                  }}
                  contentId={contentId ?? ''}
                  tenantId={tenantId ?? ''}
                  currentValue={descValue}
                  disabled={isAuthoring}
                  preview
                />
              )}
            </div>
          )}
        </div>
        {hasDescValue || manualDescOpen ? (
          <textarea
            id="article-description-row"
            value={descValue}
            onChange={handleDescChange}
            disabled={isAuthoring}
            rows={3}
            aria-label="Article description"
            className={TEXTAREA_CLASSES}
            data-testid="desc-textarea"
          />
        ) : isAuthoring ? (
          // The socket's bare disabled state is replaced by a LABELED
          // async skeleton while the run is in flight.
          <AuthoringSkeleton label="Writing article summary…" testId="desc-socket-skeleton" />
        ) : (
          <EmptySocket testId="desc-socket">
            {canSuggestDesc && (
              <AiSuggestButton
                field="article_description"
                onSuggest={(v) => {
                  if (descTimerRef.current) clearTimeout(descTimerRef.current);
                  setDescValue(v);
                  onEditField?.('article_description', v);
                }}
                contentId={contentId ?? ''}
                tenantId={tenantId ?? ''}
                currentValue={descValue}
                disabled={isAuthoring}
                preview
                variant="quiet"
                label="Suggest a summary"
              />
            )}
            <button
              type="button"
              onClick={() => setManualDescOpen(true)}
              className="text-[10.5px] text-muted-foreground underline underline-offset-2 hover:text-foreground cursor-pointer"
              data-testid="desc-write-manually"
            >
              write manually
            </button>
            {/* Postel prefill: never request information we already have.
                EXPLICIT click only — the fill routes through onEditField
                (persists to the DB), never a silent write. No client-side
                validation/normalization (liberal input is the ruling). */}
            {fallbackDescription?.trim() && (
              <button
                type="button"
                onClick={() => {
                  if (descTimerRef.current) clearTimeout(descTimerRef.current);
                  const v = fallbackDescription;
                  setDescValue(v);
                  onEditField?.('article_description', v);
                }}
                className="text-[10.5px] text-muted-foreground underline underline-offset-2 hover:text-foreground cursor-pointer"
                data-testid="desc-use-meta"
              >
                Use meta description
              </button>
            )}
          </EmptySocket>
        )}
      </div>
    </div>
  );

  const breadcrumbBody = (
    <>
      {/* The value IS the status: show the actual derived trail. The
          current page is bolded by weight only (muted stays the read-only
          cue). */}
      <p className="mt-1 text-[11px] text-foreground" data-testid="breadcrumb-trail">
        {trailWithCurrentBold(breadcrumbTrail ?? 'Home › Blog › this article')}
      </p>
      <p className="mt-0.5 text-[10px] text-muted-foreground leading-snug">
        Derived from the article URL + site facts.
      </p>
    </>
  );

  const howtoBody = authoredSchema ? (
    <HowToEditor
      authoredSchema={authoredSchema}
      onEditField={onEditField}
      onForceHowTo={onForceHowTo}
      schemaRunStatus={schemaRunStatus}
    />
  ) : isAuthoring ? (
    // Labeled async skeleton replaces the bare disabled socket state.
    <AuthoringSkeleton label="Writing how-to steps…" testId="howto-socket-skeleton" />
  ) : (
    <EmptySocket label="No how-to authored" testId="howto-socket">
      {onGenerateSchema && (
        <button
          type="button"
          onClick={onGenerateSchema}
          disabled={generating}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-semibold text-teal-dark bg-teal-light border border-teal/30 hover:bg-teal/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
          data-testid="generate-schema-btn"
        >
          <Sparkles className="size-3" />
          {generating ? 'Generating…' : 'Generate authored schema'}
        </button>
      )}
    </EmptySocket>
  );

  const faqBody =
    faqQuestionCount >= 2 ? (
      <p className="text-[11px] text-foreground" data-testid="faq-count">
        {faqQuestionCount} questions detected
        <span className="text-[10px] text-muted-foreground">
          {' '}
          — from FAQ blocks in the article body
        </span>
      </p>
    ) : faqQuestionCount === 1 ? (
      // Won't-ship honesty: a single FAQ pair ships nothing at publish.
      // Inert + informational — never a dashed slot (FAQ is never the
      // imperative slot) and never a count presented as shipping.
      <p className="text-[11px] italic text-muted-foreground" data-testid="faq-wont-ship">
        1 question found — needs at least 2 to ship
      </p>
    ) : (
      <EmptySocket
        label="No FAQ blocks in the article — add them in the body; they ship automatically"
        testId="faq-socket"
      />
    );

  const productBody = (
    <p className="text-[11px] text-muted-foreground italic" data-testid="product-note">
      Not available yet — needs a backend authored slot
    </p>
  );

  const bodies: Record<(typeof PER_TYPE_ROWS)[number], React.ReactNode> = {
    Article: articleBody,
    BreadcrumbList: breadcrumbBody,
    HowTo: howtoBody,
    FAQPage: faqBody,
    Product: productBody,
  };

  return (
    <div data-testid="per-type-rows" className="space-y-3">
      {PER_TYPE_ROWS.map((typeName) => {
        const isExpanded = accordion.expanded.includes(typeName);
        const isFocused = (focusType ?? null) === typeName;
        return (
          <div
            key={typeName}
            data-testid={`row-${typeName}`}
            data-focused={isFocused || undefined}
            className={cn(
              'rounded-md border border-border bg-card',
              typeName === 'Product' && 'opacity-80',
              // S4 "you are here" — the focused panel gets a left accent bar
              // + header contrast (Krug: twice as prominent).
              isFocused && 'border-l-[3px] border-l-teal',
            )}
          >
            <button
              type="button"
              onClick={() => togglePanel(typeName)}
              aria-expanded={isExpanded}
              aria-label={`Toggle ${TYPE_LABELS[typeName].label} panel`}
              className={cn(
                'w-full flex items-center gap-2 px-2.5 py-2 text-left cursor-pointer rounded-md',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-teal',
                isFocused && 'bg-teal-light/50',
              )}
            >
              <ChevronDown
                className={cn(
                  'size-3.5 shrink-0 text-muted-foreground transition-transform',
                  isExpanded ? 'rotate-0' : '-rotate-90',
                )}
              />
              <RowHeaderLabel typeName={typeName} />
              {SHIPS_LABELS[typeName] && <ShipsTag label={SHIPS_LABELS[typeName]} />}
              <span
                data-testid={`presence-line-${typeName}`}
                className="ml-auto pl-2 text-[10.5px] text-muted-foreground truncate max-w-[38%] text-right"
              >
                {presenceLines[typeName]}
              </span>
            </button>
            {isExpanded && (
              <div data-testid={`panel-${typeName}`} className="px-2.5 pb-2.5">
                {bodies[typeName]}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Technical view: the raw JSON-LD graph nodes, Copy JSON, the selection
 * banner, and the shadow-enrichment debug block live under ONE
 * closed-by-default disclosure at the bottom of the drawer — reviewers
 * never meet raw JSON-LD uninvited, and NOTHING is deleted. This component
 * mounts fresh with the Sheet content, so every drawer session starts with
 * the disclosure CLOSED.
 */
function TechnicalViewDisclosure({
  preview,
  fieldMap,
  selectionActive,
  copied,
  onCopyGraph,
  enrichmentJson,
  copiedEnrichment,
  onCopyEnrichment,
}: {
  preview: SchemaPreviewResponse | null | undefined;
  fieldMap: Map<string, { source: SchemaFieldSource; ships: boolean }>;
  selectionActive: boolean | null;
  copied: boolean;
  onCopyGraph: () => void;
  enrichmentJson: string | null;
  copiedEnrichment: boolean;
  onCopyEnrichment: () => void;
}) {
  const [open, setOpen] = useState(false);
  const hasGraph = preview?.jsonld != null && preview.jsonld.length > 0;
  return (
    <div className="rounded-md border border-border" data-testid="technical-view">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer rounded-md focus:outline-none focus-visible:ring-1 focus-visible:ring-teal"
      >
        <ChevronDown
          className={cn(
            'size-3.5 text-muted-foreground transition-transform',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
        <span className="text-[11.5px] font-medium text-muted-foreground">
          Technical view — the JSON-LD this platform ships
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3" data-testid="technical-view-body">
          {/* Ownership-honest note (live-schema evidence: TWO graphs ship on
              bridge tenants — the plugin's head graph + our body-injected
              graph). Duplicate-graph resolution is the pipeline lane's job. */}
          <p className="text-[10px] text-muted-foreground leading-snug">
            Your site&apos;s SEO plugin may ship additional site-level schema
            (site, page, author, breadcrumbs). This view covers only what this
            platform adds.
          </p>

          {/* ---- Selection banner (honest reword) ---- */}
          {selectionActive !== null && (
            <div
              className={cn(
                'flex items-start gap-2 px-3 py-2.5 rounded-md border text-[11.5px] leading-snug',
                selectionActive
                  ? 'bg-teal-light/50 border-teal/30 text-teal-dark'
                  : 'bg-warm-bg/50 border-border text-muted-foreground',
              )}
            >
              {selectionActive ? (
                <Check className="size-4 text-teal shrink-0 mt-0.5" />
              ) : (
                <Info className="size-4 text-muted-foreground shrink-0 mt-0.5" />
              )}
              <span>
                {selectionActive
                  ? 'Article and BreadcrumbList always ship. HowTo ships when authored; FAQ ships from body blocks. Selections reach the direct and scheduled publish paths.'
                  : 'Article and BreadcrumbList always ship. Other types currently fall back to auto-defaults on publish — selections will take effect when SCHEMA_SELECTION_PUBLISH_ENABLED is turned on.'}
              </span>
            </div>
          )}

          {/* ---- Composed graph nodes + Copy JSON ---- */}
          {hasGraph && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-bold text-foreground uppercase tracking-wider">
                  Composed graph
                </h3>
                <button
                  type="button"
                  onClick={onCopyGraph}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium text-muted-foreground border border-border bg-card hover:text-foreground cursor-pointer transition-colors"
                >
                  {copied ? (
                    <>
                      <ClipboardCheck className="size-3 text-teal-dark" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="size-3" />
                      Copy JSON
                    </>
                  )}
                </button>
              </div>
              <div className="space-y-2">
                {preview!.jsonld!.map((node, i) => (
                  <GraphNodeCard key={i} node={node} fieldMap={fieldMap} />
                ))}
              </div>
            </div>
          )}

          {/* ---- Shadow enrichment (postgen_jsonld) — debug only. Shown ONLY
              when a blob actually exists (rare; nothing produces
              postgen_jsonld today). ---- */}
          {enrichmentJson != null && (
            <div>
              <h3 className="text-[12px] font-bold text-coral-dark uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Sparkles className="size-3.5" />
                Shadow enrichment (debug)
              </h3>
              <div className="rounded-md border-2 border-coral/30 bg-coral-light/20 overflow-hidden">
                <div className="px-3 py-2 border-b border-coral/20 bg-coral-light/30">
                  <p className="text-[10.5px] font-bold text-coral-dark">
                    Shadow · tracked · NOT shipped to WordPress
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                    The <code className="font-mono">postgen_jsonld</code> blob
                    is captured during generation for review only. It has no
                    publish consumer in this release. Do not confuse with{' '}
                    <code className="font-mono">postgen_schema.fields</code>{' '}
                    (authored, which DO ship when their flag is on).
                  </p>
                </div>
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-coral/10">
                  <span className="text-[10px] text-muted-foreground italic">
                    Raw blob — representative only.
                  </span>
                  <button
                    type="button"
                    onClick={onCopyEnrichment}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium text-muted-foreground border border-border bg-card hover:text-foreground cursor-pointer transition-colors"
                  >
                    {copiedEnrichment ? (
                      <>
                        <ClipboardCheck className="size-3 text-teal-dark" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="size-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <pre className="p-2.5 text-[10.5px] font-mono leading-relaxed text-foreground whitespace-pre">
                    {enrichmentJson}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function StructuredDataDrawer({
  open,
  onOpenChange,
  contentId,
  enrichment,
  onGenerateSchema,
  generating,
  authoredSchema,
  onEditField,
  onForceHowTo,
  schemaRunStatus,
  tenantId,
  focusType,
  fallbackDescription,
}: StructuredDataDrawerProps) {
  const { data: preview, isError, isLoading } = useSchemaPreview(contentId, open);

  // ---- Resize state (mirror ResearchSessionsDrawer) ----
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Number(stored))) : DEFAULT_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    setIsDragging(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      localStorage.setItem(STORAGE_KEY, String(widthRef.current));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // ---- Field provenance lookup (per schema_type.field) ----
  const fieldMap = useMemo(() => {
    const map = new Map<string, { source: SchemaFieldSource; ships: boolean }>();
    for (const f of preview?.fields ?? []) {
      map.set(`${f.schema_type}.${f.field}`, { source: f.source, ships: f.ships });
    }
    return map;
  }, [preview?.fields]);

  // ---- Copy graph ----
  const [copied, setCopied] = useState(false);
  const handleCopyGraph = useCallback(() => {
    if (!preview?.jsonld) return;
    const text = JSON.stringify(
      { '@context': 'https://schema.org', '@graph': preview.jsonld },
      null,
      2,
    );
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }, [preview?.jsonld]);

  const [copiedEnrichment, setCopiedEnrichment] = useState(false);
  const handleCopyEnrichment = useCallback(() => {
    if (!enrichment) return;
    void navigator.clipboard?.writeText(JSON.stringify(enrichment, null, 2)).then(
      () => {
        setCopiedEnrichment(true);
        window.setTimeout(() => setCopiedEnrichment(false), 1500);
      },
      () => {},
    );
  }, [enrichment]);

  const hasError = isError || (preview?.error != null);
  const hasGraph = preview?.jsonld != null && preview.jsonld.length > 0;
  const selectionActive = preview?.selection_active ?? null;
  const enrichmentJson = useMemo(
    () => (enrichment ? JSON.stringify(enrichment, null, 2) : null),
    [enrichment],
  );

  // Authoring pill — surfaces the family-E run state at the top of the
  // composed-graph section so edits can be disabled across all per-type rows.
  const isAuthoring =
    schemaRunStatus === 'queued' || schemaRunStatus === 'running';

  // ---- D2 end-peak summary (graft-2 drawer half): a single quiet teal
  //      confirmation line when every rich-result type that ships for this
  //      article is ready. Same data sources as PerTypeRows (authoredSchema
  //      + preview.jsonld); Product is excluded ("not available yet"). ----
  const faqCountForPeak =
    authoredSchema?.faq != null
      ? authoredSchema.faq.pairs.length
      : countFaqQuestions(preview?.jsonld ?? null);

  // Article + BreadcrumbList always ship (server-enforced in T3) and are
  // always ready once the composed graph loaded. HowTo ships when authored
  // with steps; FAQPage ships at the ≥2-pair publish gate. Every type that
  // ships is ready — the count confirms completeness, nothing outstanding.
  const howToReady = Boolean(
    authoredSchema?.is_howto && (authoredSchema.howto?.steps?.length ?? 0) > 0,
  );

  const richResultsShipCount = useMemo(() => {
    if (isLoading || hasError || !hasGraph) return null;
    return 2 + (howToReady ? 1 : 0) + (faqCountForPeak >= 2 ? 1 : 0);
  }, [isLoading, hasError, hasGraph, howToReady, faqCountForPeak]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        modal={false}
        className="flex flex-col p-0"
        style={{ width: `${width}px`, maxWidth: `${width}px` }}
      >
        {/* Resize handle — left edge */}
        <div
          onMouseDown={handleResizeStart}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize z-50',
            'hover:bg-teal/30 transition-colors',
            isDragging && 'bg-teal/40',
          )}
        />

        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          {/* The title must not collide with the SERP preview card —
              "Google rich results" deliberately fails the "is this where I
              edit the snippet?" test. The description is the
              ownership-scoped conceptual model: TWO graphs ship on bridge
              tenants (the plugin's head graph + our body-injected graph),
              so the copy never claims "this is what Google receives". */}
          <SheetTitle className="flex items-center gap-2">
            <Braces className="h-5 w-5 text-teal" />
            Google rich results
            <span className="text-[10px] font-normal text-muted-foreground">
              Schema · JSON-LD
            </span>
          </SheetTitle>
          <SheetDescription>
            What this platform adds for Google when you publish — one row per
            result type; the value shown is the value shipped. Your site&apos;s
            SEO plugin may add its own site-level markup on top.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* ---- Error state (stays TOP-LEVEL — it gates the per-type
                values themselves; S4) ---- */}
            {hasError && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-md border border-coral/30 bg-coral-light/50 text-[11.5px] leading-snug text-coral-dark">
                <AlertTriangle className="size-4 text-coral shrink-0 mt-0.5" />
                <span>
                  Couldn't load the schema preview
                  {preview?.error ? `: ${preview.error}` : '.'} The drawer is
                  still open — per-type rows will populate when the endpoint is
                  reachable.
                </span>
              </div>
            )}

            {/* ---- Per-type result panels — the PRIMARY surface (S4).
                Scoped open via focusType; the raw graph, banner, and shadow
                debug block are demoted to the technical view below. ---- */}
            <div>
              {(isAuthoring || (authoredSchema && onGenerateSchema)) && (
                <div className="flex items-center justify-end gap-2 mb-2">
                  {isAuthoring && (
                    <span
                      className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-coral-dark"
                      data-testid="authoring-pill"
                    >
                      <Loader2 className="size-3 animate-spin" />
                      authoring…
                    </span>
                  )}
                  {authoredSchema && onGenerateSchema && (
                    <button
                      type="button"
                      onClick={onGenerateSchema}
                      disabled={generating || isAuthoring}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-semibold text-teal-dark bg-teal-light border border-teal/30 hover:bg-teal/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      data-testid="regenerate-schema-btn"
                    >
                      <Sparkles className="size-3" />
                      {generating ? 'Generating…' : 'Regenerate schema'}
                    </button>
                  )}
                </div>
              )}
              {isLoading && (
                <p className="text-[11.5px] text-muted-foreground py-4 text-center">
                  Loading the composed graph…
                </p>
              )}
              {!isLoading && !hasGraph && !hasError && (
                <p className="text-[11.5px] text-muted-foreground py-4 text-center">
                  No schemas would ship for this article.
                </p>
              )}
              {!isLoading && (
                <PerTypeRows
                  preview={preview}
                  fieldMap={fieldMap}
                  authoredSchema={authoredSchema}
                  onEditField={onEditField}
                  onForceHowTo={onForceHowTo}
                  schemaRunStatus={schemaRunStatus}
                  contentId={contentId}
                  tenantId={tenantId}
                  onGenerateSchema={onGenerateSchema}
                  generating={generating}
                  focusType={focusType}
                  fallbackDescription={fallbackDescription}
                />
              )}
            </div>

            {/* ---- D2 end-peak (graft-2 drawer half): when all rich-result
                types that ship are ready, one quiet teal confirmation line.
                Mirrors the card's "Ready — ships at publish" register —
                plain text, no badge, no check icon, no celebration. ---- */}
            {!isAuthoring && richResultsShipCount != null && (
              <p
                data-testid="drawer-ready-line"
                className="text-[11px] font-medium text-teal-dark"
              >
                {richResultsShipCount} of {richResultsShipCount} rich results ready
              </p>
            )}

            {/* ---- Technical view: raw JSON-LD, Copy JSON, selection
                banner, and the shadow-enrichment debug block live under ONE
                closed-by-default disclosure. Nothing deleted. ---- */}
            <TechnicalViewDisclosure
              preview={preview}
              fieldMap={fieldMap}
              selectionActive={selectionActive}
              copied={copied}
              onCopyGraph={handleCopyGraph}
              enrichmentJson={enrichmentJson}
              copiedEnrichment={copiedEnrichment}
              onCopyEnrichment={handleCopyEnrichment}
            />

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
